import React, { useState, useEffect, useCallback } from 'react';
import { reportsAPI } from '../services/api';
import { formatDisplayDate } from '../utils/dateFormat';
import './DataGrid.css';

function DataGrid({ 
  title, 
  columns, 
  fetchUrl, 
  exportUrl, 
  editable = false, 
  editableColumns = [], 
  onSaveRow,
  embedded = false,
  refreshKey = 0,
  query = {},
  showPrintButton = false,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useState('last_name');
  const [dir, setDir] = useState('asc');
  const [limit, setLimit] = useState(25);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [editingRow, setEditingRow] = useState(null);
  const [editData, setEditData] = useState({});
  const [error, setError] = useState(null);

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const queryParams = Object.fromEntries(
        Object.entries(query).filter(([_, v]) => v != null && v !== '')
      );
      const params = {
        limit,
        offset,
        sort,
        dir,
        ...(searchTerm && { q: searchTerm }),
        // Merge query params (skip null/undefined/empty string)
        ...queryParams,
      };
      const response = await reportsAPI.get(fetchUrl, params);
      setRows(response.data.rows || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Error loading data:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [fetchUrl, limit, offset, sort, dir, searchTerm, query]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshKey]);

  const handleSearch = (e) => {
    e.preventDefault();
    setOffset(0); // Reset to first page on search
    loadData();
  };

  const handleSort = (columnKey) => {
    if (sort === columnKey) {
      setDir(dir === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(columnKey);
      setDir('asc');
    }
    setOffset(0); // Reset to first page on sort
  };

  const handlePrev = () => {
    if (offset > 0) {
      setOffset(Math.max(0, offset - limit));
    }
  };

  const handleNext = () => {
    if (offset + limit < total) {
      setOffset(offset + limit);
    }
  };

  const handleExport = async () => {
    try {
      const params = {
        sort,
        dir,
        ...(searchTerm && { q: searchTerm }),
        // Merge query params (skip null/undefined/empty string)
        ...Object.fromEntries(
          Object.entries(query).filter(([_, v]) => v != null && v !== '')
        ),
      };
      const response = await reportsAPI.exportCsv(exportUrl, params);
      
      // response.data is already a blob when responseType: 'blob' is used
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = exportUrl.includes('clients') ? 'clients_export.csv' : 'export.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error exporting CSV:', err);
      alert('Failed to export CSV');
    }
  };

  const handleEdit = (row) => {
    setEditingRow(row.id);
    setEditData({ ...row });
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditData({});
  };

  const handleSave = async (rowId) => {
    try {
      setError(null);
      if (onSaveRow) {
        await onSaveRow(rowId, editData);
        setEditingRow(null);
        setEditData({});
        loadData(); // Refresh data
      }
    } catch (err) {
      console.error('Error saving row:', err);
      setError(err.response?.data?.error || 'Failed to save changes');
    }
  };

  const handleEditChange = (field, value) => {
    setEditData({ ...editData, [field]: value });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return formatDisplayDate(dateString);
  };

  return (
    <div className={`data-grid ${embedded ? 'data-grid-embedded' : ''}`}>
      {!embedded && (
        <div className="data-grid-header">
          <h2>{title}</h2>
          <div className="data-grid-actions">
            <button onClick={loadData} className="btn-refresh">Refresh</button>
            {exportUrl && (
              <button onClick={handleExport} className="btn-export">Export CSV</button>
            )}
            {showPrintButton && (
              <button type="button" onClick={() => window.print()} className="btn-print">Print</button>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="error-message">{error}</div>
      )}

      {!embedded && (
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="btn-search">Search</button>
          {searchTerm && (
            <button
              type="button"
              onClick={() => {
                setSearchTerm('');
                setOffset(0);
              }}
              className="btn-clear"
            >
              Clear
            </button>
          )}
        </form>
      )}

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      className={sort === col.key ? `sortable sort-${dir}` : 'sortable'}
                    >
                      {col.label}
                      {sort === col.key && (
                        <span className="sort-indicator">{dir === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </th>
                  ))}
                  {editable && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length + (editable ? 1 : 0)} className="no-data">
                      No data found
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.id}>
                      {columns.map((col) => {
                        // Support editKey: if column has editKey, use it for editing; otherwise use col.key
                        const editKey = col.editKey || col.key;
                        const isEditable = editable && editingRow === row.id && editableColumns.includes(editKey);
                        
                        if (isEditable) {
                          // Support dropdown/select for columns with options
                          if (col.options && Array.isArray(col.options)) {
                            // Convert value to string for select element (HTML selects use strings)
                            const currentValue = editData[editKey] !== null && editData[editKey] !== undefined 
                              ? String(editData[editKey]) 
                              : '';
                            return (
                              <td key={col.key}>
                                <select
                                  value={currentValue}
                                  onChange={(e) => handleEditChange(editKey, e.target.value)}
                                  className="edit-input"
                                >
                                  <option value="">-- Select --</option>
                                  {col.options.map((option) => (
                                    <option key={option.value} value={String(option.value)}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              </td>
                            );
                          }
                          // Default to input field
                          return (
                            <td key={col.key}>
                              <input
                                type={editKey.includes('date') ? 'date' : 'text'}
                                value={editData[editKey] || ''}
                                onChange={(e) => handleEditChange(editKey, e.target.value)}
                                className="edit-input"
                              />
                            </td>
                          );
                        }
                        const isDateCol = col.key.includes('date') || col.type === 'date';
                        const isBoolCol = col.type === 'boolean' || col.key === 'decline_1950' || col.key === 'family_interested_es' || col.key === 'accepted_es';
                        let cellValue = row[col.key];
                        if (isDateCol) {
                          cellValue = formatDate(cellValue);
                        } else if (isBoolCol && typeof cellValue === 'boolean') {
                          cellValue = cellValue ? 'Yes' : 'No';
                        } else if (cellValue !== null && cellValue !== undefined) {
                          cellValue = String(cellValue);
                        } else {
                          cellValue = '';
                        }
                        return (
                          <td key={col.key}>
                            {cellValue}
                          </td>
                        );
                      })}
                      {editable && (
                        <td>
                          {editingRow === row.id ? (
                            <>
                              <button
                                onClick={() => handleSave(row.id)}
                                className="btn-save"
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="btn-cancel"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => handleEdit(row)}
                              className="btn-edit"
                            >
                              Edit
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <div className="pagination-info">
              Showing {offset + 1} to {Math.min(offset + limit, total)} of {total}
            </div>
            <div className="pagination-controls">
              <button
                onClick={handlePrev}
                disabled={offset === 0}
                className="btn-prev"
              >
                Previous
              </button>
              <button
                onClick={handleNext}
                disabled={offset + limit >= total}
                className="btn-next"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default DataGrid;
